import { useState, useEffect, useCallback } from 'react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { getDirectory, type DirectoryResponse } from '../api/members'
import type { DirectoryMember } from '../types'

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')
const PAGE_SIZE = 50

const STATES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'Washington DC', FL: 'Florida',
  GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana',
  IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine',
  MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi',
  MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire',
  NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
  OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', PR: 'Puerto Rico',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas',
  UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia',
  WI: 'Wisconsin', WY: 'Wyoming',
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba', NB: 'New Brunswick',
  NL: 'Newfoundland', NS: 'Nova Scotia', ON: 'Ontario', PE: 'Prince Edward Island',
  QC: 'Quebec', SK: 'Saskatchewan',
}

const columnHelper = createColumnHelper<DirectoryMember>()
const columns = [
  columnHelper.accessor('first', { header: 'First', cell: (info) => info.getValue() }),
  columnHelper.accessor('last', { header: 'Last', cell: (info) => info.getValue() }),
  columnHelper.accessor('city', { header: 'City', cell: (info) => info.getValue() }),
  columnHelper.accessor('state', { header: 'State', cell: (info) => info.getValue() }),
  columnHelper.accessor('phone', { header: 'Phone', cell: (info) => info.getValue() }),
  columnHelper.accessor('email', {
    header: 'Email',
    cell: (info) => {
      const email = info.getValue()
      return email ? (
        <a href={`mailto:${email}`} className="text-primary hover:underline">{email}</a>
      ) : null
    },
  }),
]

export function Directory() {
  const [data, setData] = useState<DirectoryMember[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Filter state
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [letter, setLetter] = useState('')
  const [state, setState] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRows, setTotalRows] = useState(0)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const result: DirectoryResponse = await getDirectory({
        search: search || undefined,
        state: state || undefined,
        letter: letter || undefined,
        page,
        limit: PAGE_SIZE,
      })
      setData(result.members)
      setTotalPages(result.totalPages)
      setTotalRows(result.total)
    } catch {
      setError('Unable to load directory. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [search, state, letter, page])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    setSearch(searchInput)
    setLetter('')
    setPage(1)
  }

  function handleLetterClick(l: string) {
    setLetter(l === letter ? '' : l)
    setSearch('')
    setSearchInput('')
    setPage(1)
  }

  function handleStateChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setState(e.target.value)
    setPage(1)
  }

  function handleClear() {
    setSearch('')
    setSearchInput('')
    setLetter('')
    setState('')
    setPage(1)
  }

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div>
      <h1 className="text-2xl font-heading font-bold text-text mb-6">Member Directory</h1>

      {/* Search + state filter */}
      <div className="flex flex-wrap gap-3 items-end mb-4">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-[200px]">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name, city, state..."
            className="flex-1 max-w-sm px-3 py-2 border border-border rounded-md bg-surface text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-text-inverse text-sm rounded-md hover:bg-primary-light transition-colors"
          >
            Search
          </button>
        </form>

        <select
          value={state}
          onChange={handleStateChange}
          className="px-3 py-2 border border-border rounded-md bg-surface text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          <option value="">All States</option>
          {Object.entries(STATES).map(([abbr, name]) => (
            <option key={abbr} value={abbr}>{name} ({abbr})</option>
          ))}
        </select>

        {(search || letter || state) && (
          <button
            onClick={handleClear}
            className="px-3 py-2 text-sm text-text-muted hover:text-text border border-border rounded-md hover:bg-surface-hover transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {/* Letter filter */}
      <div className="flex flex-wrap gap-1 mb-4">
        <button
          onClick={() => handleLetterClick('')}
          className={`px-2 py-1 text-xs rounded border transition-colors ${
            !letter && !search
              ? 'bg-primary text-text-inverse border-primary'
              : 'border-border text-text hover:bg-surface-hover'
          }`}
        >
          All
        </button>
        {LETTERS.map((l) => (
          <button
            key={l}
            onClick={() => handleLetterClick(l)}
            className={`px-2 py-1 text-xs rounded border transition-colors ${
              letter === l
                ? 'bg-primary text-text-inverse border-primary'
                : 'border-border text-text hover:bg-surface-hover'
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {/* Count */}
      <p className="text-sm text-text-muted mb-3">
        {totalRows} member{totalRows !== 1 ? 's' : ''} found
      </p>

      {error && (
        <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-3 text-sm mb-4">
          {error}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto border border-border rounded-lg">
        <table className="w-full text-sm">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-primary text-text-inverse">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider"
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-text-muted">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent mr-2" />
                    Loading...
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-text-muted">
                  No members found matching your search.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-t border-border hover:bg-surface-hover transition-colors">
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-2.5">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-text-muted">
            Page {page} of {totalPages}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              First
            </button>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Prev
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
